import React from 'react';
import { Link } from 'react-router-dom';
import { Shell } from '../components/layout/Shell';
import { Button } from '../components/ui/button';
import {
  ShieldCheck, Lock, Database, EnvelopeSimple, FileText, ArrowRight,
} from '@phosphor-icons/react';

const Section = ({ title, eyebrow, children, testId }) => (
  <section className="mb-12" data-testid={testId}>
    {eyebrow && <div className="label-eyebrow text-primary mb-2">{eyebrow}</div>}
    <h2 className="font-display font-black text-2xl lg:text-3xl tracking-tighter leading-tight mb-4">
      {title}
    </h2>
    <div className="prose prose-neutral max-w-none text-sm lg:text-base leading-relaxed text-foreground space-y-3">
      {children}
    </div>
  </section>
);

const Privacy = () => {
  return (
    <Shell>
      <div className="max-w-[820px] mx-auto px-6 py-16" data-testid="privacy-page">
        <div className="mb-12">
          <div className="label-eyebrow text-primary mb-3 flex items-center gap-2">
            <ShieldCheck size={14} weight="fill" /> PRIVACY POLICY
          </div>
          <h1 className="font-display font-black text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-4">
            Plain-English privacy policy.
          </h1>
          <p className="text-muted-foreground text-base lg:text-lg leading-relaxed">
            We collect what we need to score you fairly, nothing more. You can see it,
            export it, or delete it any time. Last updated: February 2026.
          </p>
        </div>

        <Section title="What we collect" eyebrow="1 · DATA WE HOLD" testId="privacy-collect">
          <p>When you create a candidate account we ask for:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Name and email (to sign you in and send job updates)</li>
            <li>Your country and work authorization (so employers see you can legally take the role)</li>
            <li>Your projects, credentials, headline and résumé file (the proof we score against)</li>
            <li>Your assessment attempts — the raw answers you submit and the structured timing data</li>
          </ul>
          <p>When you create an employer account we ask for:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Name, email, company name</li>
            <li>The jobs you post and any custom questions you attach</li>
            <li>Your billing details (handled by Stripe — we never see your card number)</li>
          </ul>
          <p>
            We do <strong>not</strong> collect a photograph, date of birth, gender, marital
            status, religion, nationality, or social security / SIN number. Hikari is intentionally
            blind to those data points.
          </p>
        </Section>

        <Section title="How we use it" eyebrow="2 · WHAT IT POWERS" testId="privacy-use">
          <ul className="list-disc pl-5 space-y-1">
            <li>To compute your fit score against jobs you apply to</li>
            <li>To match you to relevant role families and assessments</li>
            <li>To send you the transactional emails you signed up for (job invites, status updates)</li>
            <li>To detect gaming attempts (AI-written essays, copy-pasted answers) — this protects fellow candidates</li>
            <li>To audit our scoring for fairness across countries and work-authorization groups</li>
          </ul>
          <p>
            We do <strong>not</strong> sell your data, ever. We do not use your résumé to train any
            third-party AI model. We do not share your data with advertisers.
          </p>
        </Section>

        <Section title="Who can see it" eyebrow="3 · ACCESS" testId="privacy-access">
          <ul className="list-disc pl-5 space-y-1">
            <li>You, always — log into your account and you can see and export everything we hold.</li>
            <li>Employers who post jobs you <em>apply to</em> — they see your profile, score, attempts, and matched signals. Nothing else.</li>
            <li>The Hikari engineering team — for debugging and abuse investigations only, never browsing.</li>
            <li>Our infrastructure providers (MongoDB Atlas, Cloudflare, Resend, Stripe) — strictly as data processors, under their respective DPAs.</li>
          </ul>
          <p>
            Employers <strong>never</strong> see candidates who didn't apply to them. The Hikari pool is opt-in per job.
          </p>
        </Section>

        <Section title="The AI part" eyebrow="4 · LLM USE" testId="privacy-ai">
          <p>
            We use Anthropic's Claude (Sonnet 4.5) to compute scoring explanations, parse résumés
            into structured skills, and generate the "Why this candidate?" reasoning. We pass the
            following to the model on each scoring run:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Your assessment answers (anonymised — no name or email)</li>
            <li>Your projects, credentials, headline (anonymised)</li>
            <li>The job's required skills and role family weights</li>
          </ul>
          <p>
            We <strong>do not</strong> send your name, email, or phone number to the model.
            Anthropic does not train on our API traffic per their commercial terms.
          </p>
        </Section>

        <Section title="Your rights" eyebrow="5 · CONTROL" testId="privacy-rights">
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Access:</strong> log in and view all of it; or email us for a complete export.</li>
            <li><strong>Correction:</strong> edit your profile any time; for assessment answers email us.</li>
            <li><strong>Deletion:</strong> email us at <a href="mailto:privacy@hikarihiring.com" className="text-primary underline">privacy@hikarihiring.com</a>. We delete the account and all associated data within 14 days, with the legally required exceptions (paid invoice records — kept 7 years per Canadian tax law).</li>
            <li><strong>Portability:</strong> we can hand you a JSON export of your profile and scores on request.</li>
            <li><strong>Object:</strong> you can disable Hikari Helper context-sharing in your profile settings (coming soon).</li>
          </ul>
          <p>
            We respect rights under Canada's PIPEDA, the GDPR (EEA / UK), CCPA (California), and
            equivalent regimes elsewhere.
          </p>
        </Section>

        <Section title="How long we keep it" eyebrow="6 · RETENTION" testId="privacy-retention">
          <ul className="list-disc pl-5 space-y-1">
            <li>Active accounts: indefinite, while you keep using Hikari.</li>
            <li>Inactive accounts (no login for 18 months): we email you, then delete 30 days later if no response.</li>
            <li>Deleted accounts: full purge within 14 days, except billing records (7 years).</li>
            <li>Application data (you applied to a job): kept by the employer who received it; we delete our copy 24 months after the role closes.</li>
          </ul>
        </Section>

        <Section title="Cookies" eyebrow="7 · BROWSER STORAGE" testId="privacy-cookies">
          <p>
            We use a single first-party authentication cookie (your session token) and one
            local-storage entry to remember if you've completed the Hikari Intake. That's it.
            No advertising cookies. No third-party trackers. We use PostHog for product analytics
            with anonymised identifiers — no personal data leaves your browser.
          </p>
        </Section>

        <Section title="Children" eyebrow="8 · AGE" testId="privacy-children">
          <p>
            Hikari is for people aged 16 and over. If you're younger, please don't sign up.
            If we find we have data on someone under 16, we delete it.
          </p>
        </Section>

        <Section title="Contact" eyebrow="9 · TALK TO US" testId="privacy-contact">
          <p>
            Privacy questions, deletion requests, or any concern: email{' '}
            <a href="mailto:privacy@hikarihiring.com" className="text-primary underline">
              privacy@hikarihiring.com
            </a>. We respond within 5 business days, often faster.
          </p>
          <p>
            Hikari Hiring Engine is operated from Ontario, Canada. Our data-protection officer
            can be reached at the address above.
          </p>
        </Section>

        {/* Footer nav */}
        <div className="border-t border-border pt-8 flex items-center justify-between flex-wrap gap-4">
          <Link to="/" className="label-eyebrow text-muted-foreground hover:text-foreground">
            ← Back to home
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/security" className="label-eyebrow text-muted-foreground hover:text-foreground">
              Security →
            </Link>
            <Link to="/methodology" className="label-eyebrow text-muted-foreground hover:text-foreground">
              Methodology →
            </Link>
          </div>
        </div>
      </div>
    </Shell>
  );
};

export default Privacy;
